/**
 * Fiscal → Configurações fiscais: prontidão por ponto de emissão (o que falta e onde resolver) e as
 * seções de configuração. Nada aqui define tributação — CFOP, CST e alíquotas ficam só na
 * matriz (Fiscal → Regras tributárias), que nasce vazia.
 */
import {
  CARD_BRANDS,
  FISCAL_ENVIRONMENT_LABEL,
  FISCAL_PRESENCE_LABEL,
  FISCAL_PROVIDER_LABEL,
  FISCAL_PURPOSE_LABEL,
  FISCAL_SYSTEM_TYPES,
  FISCAL_SYSTEM_TYPE_LABEL,
  FISCAL_UFS,
  MENU_PATH,
  SIMULATOR_SCENARIOS,
  SIMULATOR_SCENARIO_LABEL,
  TAX_KIND_LABEL,
  formatMoney,
  secretRefPrefix,
  toFormMoney,
  type FiscalOperation,
  type FiscalOperationUseView,
  type FiscalRecipientProfile,
  type FiscalSeriesView,
  type FiscalSettings,
  type FiscalUfRule,
  type PaymentInstitution,
  type PaymentMethodFiscal,
  type PaymentTerminal,
  type ReadinessItem,
  type SecretRefCheck,
  type TaxGroup,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useCan, useMe } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { DecimalInput } from "@/components/DecimalInput";
import { apiFetch } from "@/lib/api";
import { Checkbox, SaveError } from "../cadastros/widgets";
import {
  FISCAL_KEY,
  useFiscalAction,
  useFiscalQuery,
  useFiscalSettings,
  useOperations,
  useReadiness,
  useUses,
} from "./lib";
import { secretHint } from "./secret-hint";
import { FISCAL_CONFIG_SECTIONS, type Section } from "./sections";

const SECTIONS = FISCAL_CONFIG_SECTIONS;
const SECTION_OF: Record<string, Section | "empresa" | "matriz"> = {
  provedor: "provedor",
  dados_fiscais: "provedor",
  responsavel_tecnico: "provedor",
  comportamento: "provedor",
  operacoes: "operacoes",
  regras_uf: "regras_uf",
  series: "series",
  empresa: "empresa",
  matriz: "matriz",
};

const STATE_TONE = { pronta: "ok", incompleta: "warn", bloqueada: "error" } as const;
const STATE_LABEL = {
  pronta: "Pronta para emitir",
  incompleta: "Incompleta",
  bloqueada: "Bloqueada",
};

export function ConfiguracoesPage() {
  const [section, setSection] = useState<Section>("provedor");
  const go = (s: string) => {
    const target = SECTION_OF[s];
    if (target && target !== "empresa" && target !== "matriz") {
      setSection(target);
      document.getElementById("fiscal-secoes")?.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <div className="space-y-6">
      <PageTitle
        title="Configurações fiscais"
        description="O CaféGestor funciona normalmente sem a configuração fiscal; só a emissão fica bloqueada, com o motivo exato, até tudo estar pronto e aprovado."
      />
      <ReadinessPanel onGo={go} />
      <nav
        id="fiscal-secoes"
        aria-label="Assuntos da configuração fiscal"
        className="flex gap-1 overflow-x-auto border-b border-border/70"
      >
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            aria-pressed={section === s.key}
            onClick={() => setSection(s.key)}
            className={`-mb-px min-h-11 shrink-0 border-b-[3px] px-4 text-sm whitespace-nowrap ${
              section === s.key
                ? "border-caramel font-semibold text-foreground"
                : "border-transparent font-medium text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
      </nav>
      {section === "provedor" && <SettingsForm />}
      {section === "operacoes" && <OperationsSection />}
      {section === "regras_uf" && <UfRulesSection />}
      {section === "series" && <SeriesSection />}
      {section === "pagamentos" && <PaymentsSection />}
      {section === "perfis" && <ProfilesSection />}
    </div>
  );
}

// ---------------------------------------------------------------- prontidão
function ItemLine({ item, onGo }: { item: ReadinessItem; onGo: (s: string) => void }) {
  const target = SECTION_OF[item.section];
  return (
    <li className="flex flex-wrap items-start gap-2 text-sm">
      <span
        aria-hidden
        className={item.ok ? "text-success" : item.blocking ? "text-destructive" : "text-warning"}
      >
        {item.ok ? "✓" : "✗"}
      </span>
      <span className="flex-1">
        {item.label}
        {item.detail && <span className="block text-xs text-muted-foreground">{item.detail}</span>}
      </span>
      {!item.ok &&
        (target === "empresa" ? (
          <Link to="/admin/empresa" className="text-xs text-primary underline">
            Dados da empresa
          </Link>
        ) : target === "matriz" ? (
          <Link to="/fiscal/regras" className="text-xs text-primary underline">
            Regras tributárias
          </Link>
        ) : (
          <button className="text-xs text-primary underline" onClick={() => onGo(item.section)}>
            Resolver
          </button>
        ))}
    </li>
  );
}

function ReadinessPanel({ onGo }: { onGo: (s: string) => void }) {
  const r = useReadiness(true);
  const [showGaps, setShowGaps] = useState(false);
  if (r.isPending) return <Spinner label="Conferindo a prontidão fiscal..." />;
  if (!r.data) return <SaveError error={r.error} />;
  return (
    <Card
      title={`Prontidão fiscal — ${FISCAL_ENVIRONMENT_LABEL[r.data.environment]} · ${FISCAL_PROVIDER_LABEL[r.data.provider]}`}
    >
      <ul className="mb-4 space-y-1">
        {r.data.general.map((g) => (
          <ItemLine key={g.key} item={g} onGo={onGo} />
        ))}
      </ul>
      <div className="grid gap-3 md:grid-cols-2">
        {r.data.uses.map((u) => (
          <div key={u.use} className="rounded-md border p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <strong className="text-sm">{u.label}</strong>
              <Badge tone={STATE_TONE[u.state]}>{STATE_LABEL[u.state]}</Badge>
            </div>
            <ul className="space-y-1">
              {u.items.map((i) => (
                <ItemLine key={i.key} item={i} onGo={onGo} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      {r.data.coverage.length > 0 && (
        <div className="mt-4">
          <Button size="sm" variant="secondary" onClick={() => setShowGaps((v) => !v)}>
            {showGaps ? "Ocultar" : "Ver"} {r.data.coverage.length} combinação(ões) sem regra
            aprovada
          </Button>
          {showGaps && (
            <Table head={["Ponto", "Produto / NCM", "Destinatário", "Grupo", "Problema"]}>
              {r.data.coverage.map((g, n) => (
                <tr key={n}>
                  <td className="px-3 py-1">{g.use}</td>
                  <td className="px-3 py-1">
                    {g.product} <span className="font-mono text-xs">{g.ncm ?? "sem NCM"}</span>
                  </td>
                  <td className="px-3 py-1">{g.recipient}</td>
                  <td className="px-3 py-1">{TAX_KIND_LABEL[g.kind]}</td>
                  <td className="px-3 py-1">
                    <Badge tone="error">
                      {g.problem === "ausente" ? "sem regra" : "regras em conflito"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- utilitários de formulário
type FormState = Record<string, string | boolean | string[] | null>;
const nn = (v: string | boolean | string[] | null | undefined) =>
  typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : (v ?? null);
function TextInput({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}
function Choice({
  label,
  value,
  options,
  onChange,
  empty = "— não definido —",
}: {
  label: string;
  value: string | null;
  options: [string, string][];
  onChange: (v: string | null) => void;
  empty?: string | null;
}) {
  return (
    <Field label={label}>
      <Select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
        {empty !== null && <option value="">{empty}</option>}
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </Select>
    </Field>
  );
}
function Grid({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

// ---------------------------------------------------------------- dados fiscais e provedor
function SettingsForm() {
  const can = useCan();
  const q = useFiscalSettings();
  const [f, setF] = useState<FiscalSettings | null>(null);
  const [confirm, setConfirm] = useState("");
  useEffect(() => {
    if (q.data) setF(q.data);
  }, [q.data]);
  const save = useFiscalAction<Record<string, unknown>>(() => "/configuracao", "PUT");
  if (!f) return <Spinner />;
  const set = <K extends keyof FiscalSettings>(k: K, v: FiscalSettings[K]) =>
    setF({ ...f, [k]: v });
  const editable = can("fiscal.configuracao.editar");
  const toProduction = f.environment === "producao" && q.data?.environment !== "producao";
  return (
    <Card title="Dados fiscais, provedor e responsável técnico">
      <div className="space-y-5">
        <Grid>
          <Choice
            label="Ambiente"
            value={f.environment}
            empty={null}
            options={Object.entries(FISCAL_ENVIRONMENT_LABEL)}
            onChange={(v) => {
              const env = (v ?? "simulador") as FiscalSettings["environment"];
              setF({
                ...f,
                environment: env,
                provider: env === "simulador" ? "simulado" : "focus_nfe",
              });
            }}
          />
          <Choice
            label="Provedor"
            value={f.provider}
            empty={null}
            options={(f.environment === "simulador"
              ? ["desligado", "simulado"]
              : ["desligado", "focus_nfe"]
            ).map((p) => [p, FISCAL_PROVIDER_LABEL[p as FiscalSettings["provider"]]])}
            onChange={(v) => set("provider", (v ?? "desligado") as FiscalSettings["provider"])}
          />
          {f.provider === "simulado" && (
            <Choice
              label="Comportamento do simulador"
              value={f.simulatorScenario}
              empty={null}
              options={SIMULATOR_SCENARIOS.map((s) => [s, SIMULATOR_SCENARIO_LABEL[s]])}
              onChange={(v) =>
                set("simulatorScenario", (v ?? "autorizar") as FiscalSettings["simulatorScenario"])
              }
            />
          )}
        </Grid>
        <Alert tone="info">
          Segredos (token do provedor, segredo do aviso, CSRT) NUNCA são digitados aqui: informe só
          o NOME da variável do servidor (começando por FISCAL_). O valor fica no servidor da
          API/worker.
        </Alert>
        <Grid>
          <SecretRef
            label="Token do provedor (variável)"
            value={f.providerTokenRef}
            saved={q.data?.providerTokenRef ?? null}
            present={f.secretsPresent.providerToken}
            onChange={(v) => set("providerTokenRef", v)}
          />
          <SecretRef
            label="Segredo do aviso/webhook (variável)"
            value={f.webhookSecretRef}
            saved={q.data?.webhookSecretRef ?? null}
            present={f.secretsPresent.webhookSecret}
            onChange={(v) => set("webhookSecretRef", v)}
          />
        </Grid>
        <h3 className="font-semibold">Dados fiscais da empresa</h3>
        <Grid>
          <Choice
            label="Regime de PIS/COFINS"
            value={f.pisCofinsRegime}
            options={[
              ["cumulativo", "Cumulativo"],
              ["nao_cumulativo", "Não cumulativo"],
            ]}
            onChange={(v) => set("pisCofinsRegime", v as FiscalSettings["pisCofinsRegime"])}
          />
          <Choice
            label="Contribuinte de IPI"
            value={f.ipiContributor === null ? null : String(f.ipiContributor)}
            options={[
              ["true", "Sim"],
              ["false", "Não"],
            ]}
            onChange={(v) => set("ipiContributor", v === null ? null : v === "true")}
          />
          <TextInput
            label="CNAE (7 dígitos)"
            value={f.cnae}
            onChange={(v) => set("cnae", nn(v) as string | null)}
          />
          <TextInput
            label="E-mail para cópia dos XMLs"
            value={f.xmlEmail}
            onChange={(v) => set("xmlEmail", nn(v) as string | null)}
          />
          <TextInput
            label="ID do CSC (NFC-e)"
            value={f.nfceCscId}
            onChange={(v) => set("nfceCscId", nn(v) as string | null)}
            hint="O código CSC em si fica no provedor."
          />
        </Grid>
        <h3 className="font-semibold">Responsável técnico (infRespTec)</h3>
        <p className="text-xs text-muted-foreground">
          Não é preenchido com o CNPJ da fábrica automaticamente: informe quem é o responsável
          técnico pelo sistema emissor.
        </p>
        <Grid>
          <TextInput
            label="CNPJ"
            value={f.techRespCnpj}
            onChange={(v) => set("techRespCnpj", nn(v) as string | null)}
          />
          <TextInput
            label="Contato"
            value={f.techRespContact}
            onChange={(v) => set("techRespContact", nn(v) as string | null)}
          />
          <TextInput
            label="E-mail"
            value={f.techRespEmail}
            onChange={(v) => set("techRespEmail", nn(v) as string | null)}
          />
          <TextInput
            label="Telefone (só números)"
            value={f.techRespPhone}
            onChange={(v) => set("techRespPhone", nn(v) as string | null)}
          />
          <TextInput
            label="ID do CSRT"
            value={f.csrtId}
            onChange={(v) => set("csrtId", nn(v) as string | null)}
          />
          <SecretRef
            label="CSRT (variável)"
            value={f.csrtRef}
            saved={q.data?.csrtRef ?? null}
            present={f.secretsPresent.csrt}
            onChange={(v) => set("csrtRef", v)}
          />
        </Grid>
        <h3 className="font-semibold">Comportamento (tudo começa desligado)</h3>
        <div className="flex flex-col gap-2">
          <Checkbox
            label="Exigir remessa autorizada da carga antes das vendas da rota (e retorno antes de fechar o acerto)"
            checked={f.requireLoadRemittance}
            onChange={(e) => set("requireLoadRemittance", e.target.checked)}
          />
          <Checkbox
            label="Permitir NFC-e na rota (depende também da regra da UF)"
            checked={f.routeNfceEnabled}
            onChange={(e) => set("routeNfceEnabled", e.target.checked)}
          />
          <Checkbox
            label="Entregas sob remessa"
            checked={f.deliveryUnderRemittance}
            onChange={(e) => set("deliveryUnderRemittance", e.target.checked)}
          />
          <Checkbox
            label="Enviar a nota por e-mail ao cliente (via provedor)"
            checked={f.emailCustomer}
            onChange={(e) => set("emailCustomer", e.target.checked)}
          />
        </div>
        {toProduction && (
          <Field label="Digite PRODUCAO para confirmar a emissão com valor fiscal">
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
        )}
        {f.productionEnabledAt && (
          <p className="text-xs text-muted-foreground">
            Produção habilitada por {f.productionEnabledByEmail} em {f.productionEnabledAt}.
          </p>
        )}
        {editable && (
          <Button
            disabled={save.isPending}
            onClick={() => {
              const {
                secretsPresent: _s,
                productionEnabledAt: _a,
                productionEnabledByEmail: _b,
                exists: _e,
                ...rest
              } = f;
              save.mutate({
                ...rest,
                version: f.exists ? f.version : undefined,
                ...(toProduction ? { confirmProduction: confirm } : {}),
              });
            }}
          >
            Salvar
          </Button>
        )}
        {save.isSuccess && <Alert tone="ok">Configuração salva.</Alert>}
        <SaveError error={save.error} />
      </div>
    </Card>
  );
}

/**
 * Referência de segredo (só o NOME da variável). v7.1-hotfix2: a situação mostrada é a do nome
 * DIGITADO, conferida na API (formato, empresa, variável presente) — antes, o aviso mostrava a
 * situação do nome SALVO e dizia "não encontrada" enquanto o novo nome não era salvo.
 */
function SecretRef({
  label,
  value,
  saved,
  present,
  onChange,
}: {
  label: string;
  value: string | null;
  /** o nome salvo (a que `present` se refere) */
  saved: string | null;
  present: boolean;
  onChange: (v: string | null) => void;
}) {
  const me = useMe();
  const can = useCan();
  const prefix = secretRefPrefix(me.data?.activeCompany?.id ?? "");
  const [typed, setTyped] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setTyped(value), 400);
    return () => clearTimeout(id);
  }, [value]);
  const check = useQuery({
    queryKey: [...FISCAL_KEY, "/configuracao/segredo", typed],
    queryFn: () =>
      apiFetch<SecretRefCheck>(
        `/fiscal/configuracao/segredo?ref=${encodeURIComponent(typed ?? "")}`,
      ),
    enabled: !!typed && can("fiscal.configuracao.editar"),
  });
  const result = check.data && typed === value ? check.data : null;
  const checking = !!value && (typed !== value || check.isFetching);
  const { hint, error } = secretHint({ value, saved, present, check: result, checking, prefix });
  return (
    <Field label={label} hint={hint} error={error}>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.trim().toUpperCase() || null)}
        placeholder={`${prefix}TOKEN`}
        autoComplete="off"
        aria-invalid={error !== undefined}
      />
    </Field>
  );
}

// ---------------------------------------------------------------- operações e usos
const EMPTY_OP: FormState = {
  code: "",
  name: "",
  systemType: "venda",
  nature: "",
  direction: null,
  purpose: null,
  presence: null,
  finalConsumerMode: null,
  allowedModels: [],
  defaultModel: null,
  referenceRequired: "nenhuma",
  auxiliaryLayout: null,
  emissionFlow: "previa_confirmacao",
  paymentMode: null,
  itemValueSource: null,
  notes: "",
  status: "active",
};

function OperationsSection() {
  const can = useCan();
  const ops = useOperations();
  const uses = useUses();
  const [editing, setEditing] = useState<FiscalOperation | "nova" | null>(null);
  const setUse = useFiscalAction<{ use: string; operationId: string | null }>(
    (b) => `/usos/${b.use}`,
    "PUT",
    undefined,
    "Uso fiscal atualizado com sucesso.",
  );
  const editable = can("fiscal.configuracao.editar");
  return (
    <div className="space-y-6">
      <Card title="Usos: qual operação cada ponto do sistema utiliza">
        <Table head={["Ponto de emissão", "Tipo exigido", "Operação"]}>
          {uses.data?.map((u: FiscalOperationUseView) => (
            <tr key={u.use}>
              <td className="px-3 py-2">{u.label}</td>
              <td className="px-3 py-2">{FISCAL_SYSTEM_TYPE_LABEL[u.systemType]}</td>
              <td className="px-3 py-2">
                <Select
                  disabled={!editable}
                  value={u.operationId ?? ""}
                  onChange={(e) =>
                    setUse.mutate({ use: u.use, operationId: e.target.value || null })
                  }
                >
                  <option value="">— nenhuma (emissão bloqueada) —</option>
                  {ops.data
                    ?.filter((o) => o.systemType === u.systemType && o.status === "active")
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.code} — {o.name}
                      </option>
                    ))}
                </Select>
              </td>
            </tr>
          ))}
        </Table>
        <SaveError error={setUse.error} />
      </Card>
      <Card
        title="Operações fiscais"
        actions={
          editable && (
            <Button size="sm" onClick={() => setEditing("nova")}>
              Nova operação
            </Button>
          )
        }
      >
        {editing && (
          <OperationForm
            key={editing === "nova" ? "nova" : editing.id}
            op={editing === "nova" ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
        <Table head={["Código", "Nome", "Tipo do sistema", "Modelos", "Falta", "Situação", ""]}>
          {ops.data?.map((o) => (
            <tr key={o.id}>
              <td className="px-3 py-2 font-mono">{o.code}</td>
              <td className="px-3 py-2">{o.name}</td>
              <td className="px-3 py-2">{FISCAL_SYSTEM_TYPE_LABEL[o.systemType]}</td>
              <td className="px-3 py-2">{o.allowedModels.join(", ") || "—"}</td>
              <td className="px-3 py-2 text-xs">
                {o.missing.length ? o.missing.join(", ") : <Badge tone="ok">completa</Badge>}
              </td>
              <td className="px-3 py-2">{o.status === "active" ? "Ativa" : "Inativa"}</td>
              <td className="px-3 py-2 text-right">
                {editable && (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(o)}>
                    Editar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function OperationForm({ op, onClose }: { op: FiscalOperation | null; onClose: () => void }) {
  const [f, setF] = useState<FormState>(() => {
    if (!op) return EMPTY_OP;
    const { id: _id, version: _v, missing: _m, ...fields } = op;
    return { ...EMPTY_OP, ...fields, nature: op.nature ?? "", notes: op.notes ?? "" };
  });
  const save = useFiscalAction<Record<string, unknown>>(
    () => (op ? `/operacoes/${op.id}` : "/operacoes"),
    op ? "PUT" : "POST",
    onClose,
    op ? "Operação fiscal atualizada com sucesso." : "Operação fiscal criada com sucesso.",
  );
  const set = (k: string) => (v: string | null) => setF({ ...f, [k]: v });
  const models = (f["allowedModels"] as string[]) ?? [];
  return (
    <div className="mb-4 space-y-3 rounded-md border p-4">
      <Grid>
        <TextInput label="Código" value={f["code"] as string} onChange={set("code")} />
        <TextInput label="Nome" value={f["name"] as string} onChange={set("name")} />
        <Choice
          label="Tipo do sistema (não muda depois)"
          value={f["systemType"] as string}
          empty={null}
          options={FISCAL_SYSTEM_TYPES.map((t) => [t, FISCAL_SYSTEM_TYPE_LABEL[t]])}
          onChange={(v) => !op && set("systemType")(v)}
        />
        <TextInput
          label="Natureza da operação"
          value={f["nature"] as string}
          onChange={set("nature")}
        />
        <Choice
          label="Entrada/saída"
          value={f["direction"] as string | null}
          options={[
            ["saida", "Saída"],
            ["entrada", "Entrada"],
          ]}
          onChange={set("direction")}
        />
        <Choice
          label="Finalidade"
          value={f["purpose"] as string | null}
          options={Object.entries(FISCAL_PURPOSE_LABEL)}
          onChange={set("purpose")}
        />
        <Choice
          label="Presença do comprador"
          value={f["presence"] as string | null}
          options={Object.entries(FISCAL_PRESENCE_LABEL)}
          onChange={set("presence")}
        />
        <Choice
          label="Consumidor final"
          value={f["finalConsumerMode"] as string | null}
          options={[
            ["cliente", "Conforme o cadastro do cliente"],
            ["sim", "Sempre"],
            ["nao", "Nunca"],
          ]}
          onChange={set("finalConsumerMode")}
        />
        <Field label="Modelos permitidos">
          <div className="flex gap-4 pt-2">
            {(["55", "65"] as const).map((m) => (
              <Checkbox
                key={m}
                label={m === "55" ? "NF-e (55)" : "NFC-e (65)"}
                checked={models.includes(m)}
                onChange={(e) =>
                  setF({
                    ...f,
                    allowedModels: e.target.checked
                      ? [...models, m]
                      : models.filter((x) => x !== m),
                    defaultModel: e.target.checked
                      ? (f["defaultModel"] ?? null)
                      : f["defaultModel"] === m
                        ? null
                        : (f["defaultModel"] ?? null),
                  })
                }
              />
            ))}
          </div>
        </Field>
        <Choice
          label="Modelo padrão"
          value={f["defaultModel"] as string | null}
          options={models.map((m) => [m, m])}
          onChange={set("defaultModel")}
        />
        <Choice
          label="Nota referenciada"
          value={f["referenceRequired"] as string}
          empty={null}
          options={[
            ["nenhuma", "Nenhuma"],
            ["remessa_da_carga", "Remessa da carga"],
            ["documento_original", "Nota original"],
          ]}
          onChange={set("referenceRequired")}
        />
        <Choice
          label="Pagamento"
          value={f["paymentMode"] as string | null}
          options={[
            ["da_venda", "O da venda"],
            ["sem_pagamento", "Sem pagamento (90)"],
          ]}
          onChange={set("paymentMode")}
        />
        <Choice
          label="Valor dos itens (remessa, retorno, divergência)"
          value={f["itemValueSource"] as string | null}
          options={[
            ["custo_medio", "Custo médio"],
            ["preco_tabela", "Tabela de preço padrão"],
            ["preco_venda", "Preço da venda"],
          ]}
          onChange={set("itemValueSource")}
        />
        <Choice
          label="Documento auxiliar"
          value={f["auxiliaryLayout"] as string | null}
          options={[
            ["danfe", "DANFE"],
            ["danfe_simplificado", "DANFE simplificado"],
            ["danfe_etiqueta", "DANFE etiqueta"],
            ["danfce", "DANFE NFC-e"],
          ]}
          onChange={set("auxiliaryLayout")}
        />
        <Choice
          label="Fluxo"
          value={f["emissionFlow"] as string}
          empty={null}
          options={[
            ["previa_confirmacao", "Prévia e confirmação"],
            ["automatica", "Automática"],
          ]}
          onChange={set("emissionFlow")}
        />
        <Choice
          label="Situação"
          value={f["status"] as string}
          empty={null}
          options={[
            ["active", "Ativa"],
            ["inactive", "Inativa"],
          ]}
          onChange={set("status")}
        />
      </Grid>
      <div className="flex gap-2">
        <Button
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              ...f,
              nature: nn(f["nature"]),
              notes: nn(f["notes"]),
              ...(op ? { version: op.version } : {}),
            })
          }
        >
          Salvar
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      <SaveError error={save.error} />
    </div>
  );
}

// ---------------------------------------------------------------- regras por UF
function UfRulesSection() {
  const can = useCan();
  const rules = useFiscalQuery<FiscalUfRule[]>("/regras-uf");
  const [editing, setEditing] = useState<FiscalUfRule | "nova" | null>(null);
  return (
    <Card
      title="Regras por UF (com vigência)"
      actions={
        can("fiscal.configuracao.editar") && (
          <Button size="sm" onClick={() => setEditing("nova")}>
            Nova regra de UF
          </Button>
        )
      }
    >
      <Alert tone="info">
        Limites, prazos e exigências de cada UF (NFC-e fora do estabelecimento, identificação do
        comprador, prazo de cancelamento, vínculo do pagamento, responsável técnico, IBS/CBS) vêm
        daqui — nunca de datas fixas no código.
      </Alert>
      {editing && (
        <UfRuleForm
          key={editing === "nova" ? "n" : editing.id}
          rule={editing === "nova" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Table
        head={[
          "UF",
          "Vigência",
          "NFC-e fora",
          "Limite sem identificação",
          "Cancelamento",
          "Exigências",
          "",
        ]}
      >
        {rules.data?.map((r) => (
          <tr key={r.id}>
            <td className="px-3 py-2">{r.uf}</td>
            <td className="px-3 py-2">
              {r.validFrom} a {r.validTo ?? "…"}
            </td>
            <td className="px-3 py-2">{r.nfceOutsideEstablishment ? "sim" : "não"}</td>
            <td className="px-3 py-2">
              {r.nfceMaxAmountUnidentified === null
                ? "—"
                : formatMoney(r.nfceMaxAmountUnidentified)}
            </td>
            <td className="px-3 py-2 text-xs">
              NF-e {r.nfeCancelHours ?? "—"} h · NFC-e {r.nfceCancelMinutes ?? "—"} min
            </td>
            <td className="px-3 py-2 text-xs">
              {[
                r.requiresTechResp && "resp. técnico",
                r.requiresCsrt && "CSRT",
                r.requiresPaymentLink && "vínculo do pagamento",
                r.ibsCbsRequired && "IBS/CBS",
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </td>
            <td className="px-3 py-2 text-right">
              {can("fiscal.configuracao.editar") && (
                <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                  Editar
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

export function UfRuleForm({ rule, onClose }: { rule: FiscalUfRule | null; onClose: () => void }) {
  const [f, setF] = useState<FiscalUfRule | Omit<FiscalUfRule, "id" | "version">>(
    // valor em reais da API ("10000.00") aparece no padrão brasileiro ("10.000,00")
    rule
      ? { ...rule, nfceMaxAmountUnidentified: toFormMoney(rule.nfceMaxAmountUnidentified) || null }
      : {
          uf: "GO",
          validFrom: new Date().toISOString().slice(0, 10),
          validTo: null,
          nfceOutsideEstablishment: false,
          nfceForContributor: false,
          nfceMaxAmountUnidentified: null,
          nfceCancelMinutes: null,
          nfeCancelHours: null,
          requiresTechResp: false,
          requiresCsrt: false,
          requiresPaymentLink: false,
          ibsCbsRequired: false,
          notes: null,
        },
  );
  const save = useFiscalAction<Record<string, unknown>>(
    () => (rule ? `/regras-uf/${rule.id}` : "/regras-uf"),
    rule ? "PUT" : "POST",
    onClose,
    rule ? "Regra da UF atualizada com sucesso." : "Regra da UF criada com sucesso.",
  );
  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  return (
    <div className="my-4 space-y-3 rounded-md border p-4">
      <Grid>
        <Choice
          label="UF"
          value={f.uf}
          empty={null}
          options={FISCAL_UFS.map((u) => [u, u])}
          onChange={(v) => !rule && setF({ ...f, uf: (v ?? "GO") as FiscalUfRule["uf"] })}
        />
        <Field label="Início da vigência">
          <Input
            type="date"
            value={f.validFrom}
            onChange={(e) => setF({ ...f, validFrom: e.target.value })}
          />
        </Field>
        <Field label="Fim da vigência (vazio = em aberto)">
          <Input
            type="date"
            value={f.validTo ?? ""}
            onChange={(e) => setF({ ...f, validTo: e.target.value || null })}
          />
        </Field>
        <Field label="NFC-e: valor máximo sem identificar o comprador (R$)" hint="Ex.: 10.000,00">
          <DecimalInput
            value={f.nfceMaxAmountUnidentified ?? ""}
            onChange={(e) =>
              setF({ ...f, nfceMaxAmountUnidentified: nn(e.target.value) as string | null })
            }
          />
        </Field>
        <TextInput
          label="Prazo de cancelamento NF-e (horas)"
          value={f.nfeCancelHours === null ? "" : String(f.nfeCancelHours)}
          onChange={(v) => setF({ ...f, nfeCancelHours: num(v) })}
        />
        <TextInput
          label="Prazo de cancelamento NFC-e (minutos)"
          value={f.nfceCancelMinutes === null ? "" : String(f.nfceCancelMinutes)}
          onChange={(v) => setF({ ...f, nfceCancelMinutes: num(v) })}
        />
      </Grid>
      <div className="flex flex-col gap-2">
        <Checkbox
          label="NFC-e permitida fora do estabelecimento (rota)"
          checked={f.nfceOutsideEstablishment}
          onChange={(e) => setF({ ...f, nfceOutsideEstablishment: e.target.checked })}
        />
        <Checkbox
          label="NFC-e permitida para destinatário contribuinte"
          checked={f.nfceForContributor}
          onChange={(e) => setF({ ...f, nfceForContributor: e.target.checked })}
        />
        <Checkbox
          label="Exige responsável técnico"
          checked={f.requiresTechResp}
          onChange={(e) => setF({ ...f, requiresTechResp: e.target.checked })}
        />
        <Checkbox
          label="Exige CSRT"
          checked={f.requiresCsrt}
          onChange={(e) => setF({ ...f, requiresCsrt: e.target.checked })}
        />
        <Checkbox
          label="Exige vínculo do pagamento eletrônico (instituição e identificadores da transação)"
          checked={f.requiresPaymentLink}
          onChange={(e) => setF({ ...f, requiresPaymentLink: e.target.checked })}
        />
        <Checkbox
          label="Exige IBS/CBS no documento"
          checked={f.ibsCbsRequired}
          onChange={(e) => setF({ ...f, ibsCbsRequired: e.target.checked })}
        />
      </div>
      <div className="flex gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate({ ...f })}>
          Salvar
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      <SaveError error={save.error} />
    </div>
  );
}

// ---------------------------------------------------------------- séries
export function SeriesSection() {
  const can = useCan();
  const series = useFiscalQuery<FiscalSeriesView[]>("/series");
  const emptySeries = { model: "55", environment: "simulador", series: "1", nextNumber: "1" };
  const [f, setF] = useState(emptySeries);
  const create = useFiscalAction<Record<string, unknown>>(
    () => "/series",
    "POST",
    () => setF(emptySeries),
    "Série fiscal criada com sucesso.",
  );
  const update = useFiscalAction<FiscalSeriesView & { id: string }>(
    (b) => `/series/${b.id}`,
    "PUT",
    undefined,
    "Série fiscal atualizada com sucesso.",
  );
  return (
    <Card title="Séries e numeração">
      <Alert tone="info">
        O número é dado pelo banco na criação do documento, sem repetição nem buraco. Depois da
        primeira nota, série e próximo número não mudam (crie outra série se precisar).
      </Alert>
      <Table
        head={["Modelo", "Ambiente", "Série", "Próximo número", "Em uso desde", "Situação", ""]}
      >
        {series.data?.map((s) => (
          <tr key={s.id}>
            <td className="px-3 py-2">{s.model}</td>
            <td className="px-3 py-2">{FISCAL_ENVIRONMENT_LABEL[s.environment]}</td>
            <td className="px-3 py-2">{s.series}</td>
            <td className="px-3 py-2">{s.nextNumber}</td>
            <td className="px-3 py-2">{s.firstUsedAt ?? "—"}</td>
            <td className="px-3 py-2">{s.status === "active" ? "Ativa" : "Inativa"}</td>
            <td className="px-3 py-2 text-right">
              {can("fiscal.configuracao.editar") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    update.mutate({ ...s, status: s.status === "active" ? "inactive" : "active" })
                  }
                >
                  {s.status === "active" ? "Inativar" : "Ativar"}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <SaveError error={update.error} />
      {can("fiscal.configuracao.editar") && (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Choice
            label="Modelo"
            value={f.model}
            empty={null}
            options={[
              ["55", "55 — NF-e"],
              ["65", "65 — NFC-e"],
            ]}
            onChange={(v) => setF({ ...f, model: v ?? "55" })}
          />
          <Choice
            label="Ambiente"
            value={f.environment}
            empty={null}
            options={Object.entries(FISCAL_ENVIRONMENT_LABEL)}
            onChange={(v) => setF({ ...f, environment: v ?? "simulador" })}
          />
          <TextInput label="Série" value={f.series} onChange={(v) => setF({ ...f, series: v })} />
          <TextInput
            label="Próximo número"
            value={f.nextNumber}
            onChange={(v) => setF({ ...f, nextNumber: v })}
          />
          <Button
            disabled={create.isPending}
            onClick={() =>
              create.mutate({
                model: f.model,
                environment: f.environment,
                series: Number(f.series),
                nextNumber: Number(f.nextNumber),
              })
            }
          >
            Criar série
          </Button>
        </div>
      )}
      <SaveError error={create.error} />
    </Card>
  );
}

// ---------------------------------------------------------------- pagamentos eletrônicos
function PaymentsSection() {
  const can = useCan();
  const editable = can("fiscal.configuracao.editar");
  const inst = useFiscalQuery<PaymentInstitution[]>("/pagamentos/instituicoes");
  const terms = useFiscalQuery<PaymentTerminal[]>("/pagamentos/terminais");
  const methods = useFiscalQuery<PaymentMethodFiscal[]>("/pagamentos/formas");
  const [ni, setNi] = useState({ name: "", cnpj: "", kind: "credenciadora", uf: "" });
  const [nt, setNt] = useState({ institutionId: "", identifier: "", label: "" });
  const createInst = useFiscalAction<Record<string, unknown>>(
    () => "/pagamentos/instituicoes",
    "POST",
    () => setNi({ name: "", cnpj: "", kind: "credenciadora", uf: "" }),
    "Instituição de pagamento criada com sucesso.",
  );
  const createTerm = useFiscalAction<Record<string, unknown>>(
    () => "/pagamentos/terminais",
    "POST",
    () => setNt({ institutionId: "", identifier: "", label: "" }),
    "Terminal criado com sucesso.",
  );
  const saveMethod = useFiscalAction<PaymentMethodFiscal>(
    (b) => `/pagamentos/formas/${b.paymentMethodId}`,
    "PUT",
    undefined,
    "Dados fiscais da forma de pagamento salvos com sucesso.",
  );
  return (
    <div className="space-y-6">
      <Card title="Formas de pagamento: dados fiscais">
        <Alert tone="info">
          Só registra o necessário para a nota (instituição, bandeira, autorização, identificadores
          da transação). Nenhuma baixa financeira acontece no módulo fiscal.
        </Alert>
        <Table
          head={[
            "Forma",
            "tPag",
            "Eletrônica",
            "Integração",
            "Instituição padrão",
            "Bandeira",
            "Exige",
            "",
          ]}
        >
          {methods.data?.map((m) => (
            <MethodRow
              key={m.paymentMethodId}
              m={m}
              institutions={inst.data ?? []}
              editable={editable}
              onSave={(x) => saveMethod.mutate(x)}
            />
          ))}
        </Table>
        <SaveError error={saveMethod.error} />
      </Card>
      <Card title="Instituições (credenciadoras, PSP do PIX, bancos)">
        <Table head={["Nome", "CNPJ", "Tipo", "UF"]}>
          {inst.data?.map((i) => (
            <tr key={i.id}>
              <td className="px-3 py-2">{i.name}</td>
              <td className="px-3 py-2 font-mono">{i.cnpj}</td>
              <td className="px-3 py-2">{i.kind}</td>
              <td className="px-3 py-2">{i.uf ?? "—"}</td>
            </tr>
          ))}
        </Table>
        {editable && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <TextInput label="Nome" value={ni.name} onChange={(v) => setNi({ ...ni, name: v })} />
            <TextInput
              label="CNPJ"
              value={ni.cnpj}
              onChange={(v) => setNi({ ...ni, cnpj: v.replace(/\D/g, "") })}
            />
            <Choice
              label="Tipo"
              value={ni.kind}
              empty={null}
              options={[
                ["credenciadora", "Credenciadora (cartão)"],
                ["psp_pix", "PSP do PIX"],
                ["banco", "Banco"],
                ["outro", "Outro"],
              ]}
              onChange={(v) => setNi({ ...ni, kind: v ?? "outro" })}
            />
            <Choice
              label="UF"
              value={ni.uf || null}
              options={FISCAL_UFS.map((u) => [u, u])}
              onChange={(v) => setNi({ ...ni, uf: v ?? "" })}
            />
            <Button
              onClick={() =>
                createInst.mutate({
                  name: ni.name,
                  cnpj: ni.cnpj,
                  kind: ni.kind,
                  uf: ni.uf || null,
                })
              }
            >
              Adicionar
            </Button>
          </div>
        )}
        <SaveError error={createInst.error} />
      </Card>
      <Card title="Terminais (maquininhas)">
        <Table head={["Instituição", "Identificador", "Descrição"]}>
          {terms.data?.map((t) => (
            <tr key={t.id}>
              <td className="px-3 py-2">{t.institutionName}</td>
              <td className="px-3 py-2 font-mono">{t.identifier}</td>
              <td className="px-3 py-2">{t.label ?? "—"}</td>
            </tr>
          ))}
        </Table>
        {editable && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Choice
              label="Instituição"
              value={nt.institutionId || null}
              options={(inst.data ?? []).map((i) => [i.id, i.name])}
              onChange={(v) => setNt({ ...nt, institutionId: v ?? "" })}
            />
            <TextInput
              label="Identificador"
              value={nt.identifier}
              onChange={(v) => setNt({ ...nt, identifier: v })}
            />
            <TextInput
              label="Descrição"
              value={nt.label}
              onChange={(v) => setNt({ ...nt, label: v })}
            />
            <Button
              onClick={() =>
                createTerm.mutate({
                  institutionId: nt.institutionId,
                  identifier: nt.identifier,
                  label: nt.label || null,
                })
              }
            >
              Adicionar
            </Button>
          </div>
        )}
        <SaveError error={createTerm.error} />
      </Card>
    </div>
  );
}

function MethodRow({
  m,
  institutions,
  editable,
  onSave,
}: {
  m: PaymentMethodFiscal;
  institutions: PaymentInstitution[];
  editable: boolean;
  onSave: (m: PaymentMethodFiscal) => void;
}) {
  const [f, setF] = useState(m);
  useEffect(() => setF(m), [m]);
  return (
    <tr>
      <td className="px-3 py-2">{m.paymentMethodName}</td>
      <td className="px-3 py-2 font-mono">{m.tpag}</td>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          disabled={!editable}
          checked={f.electronic}
          onChange={(e) =>
            setF(
              e.target.checked
                ? { ...f, electronic: true }
                : {
                    ...f,
                    electronic: false,
                    integrationType: null,
                    defaultInstitutionId: null,
                    cardBrand: null,
                    requiresAuthorization: false,
                    requiresTransactionId: false,
                  },
            )
          }
        />
      </td>
      <td className="px-3 py-2">
        <Select
          disabled={!editable || !f.electronic}
          value={f.integrationType ?? ""}
          onChange={(e) =>
            setF({
              ...f,
              integrationType: (e.target.value || null) as PaymentMethodFiscal["integrationType"],
            })
          }
        >
          <option value="">—</option>
          <option value="1">Integrado (TEF/PIX API)</option>
          <option value="2">Não integrado (POS)</option>
        </Select>
      </td>
      <td className="px-3 py-2">
        <Select
          disabled={!editable || !f.electronic}
          value={f.defaultInstitutionId ?? ""}
          onChange={(e) => setF({ ...f, defaultInstitutionId: e.target.value || null })}
        >
          <option value="">—</option>
          {institutions.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-3 py-2">
        <Select
          disabled={!editable || !f.electronic}
          value={f.cardBrand ?? ""}
          onChange={(e) => setF({ ...f, cardBrand: e.target.value || null })}
        >
          <option value="">—</option>
          {Object.entries(CARD_BRANDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-3 py-2 text-xs">
        <label className="block">
          <input
            type="checkbox"
            disabled={!editable || !f.electronic}
            checked={f.requiresAuthorization}
            onChange={(e) => setF({ ...f, requiresAuthorization: e.target.checked })}
          />{" "}
          autorização/NSU
        </label>
        <label className="block">
          <input
            type="checkbox"
            disabled={!editable || !f.electronic}
            checked={f.requiresTransactionId}
            onChange={(e) => setF({ ...f, requiresTransactionId: e.target.checked })}
          />{" "}
          id. da transação (PIX)
        </label>
      </td>
      <td className="px-3 py-2 text-right">
        {editable && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSave({ ...f, version: f.configured ? f.version : null })}
          >
            Salvar
          </Button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- perfis e grupos
function ProfilesSection() {
  const can = useCan();
  const profiles = useFiscalQuery<FiscalRecipientProfile[]>("/perfis");
  const groups = useFiscalQuery<TaxGroup[]>("/grupos");
  const [p, setP] = useState<{
    name: string;
    personKind: string | null;
    ieIndicator: string | null;
    finalConsumer: string | null;
  }>({ name: "", personKind: null, ieIndicator: null, finalConsumer: null });
  const [g, setG] = useState({ code: "", name: "" });
  const createProfile = useFiscalAction<Record<string, unknown>>(
    () => "/perfis",
    "POST",
    () => setP({ name: "", personKind: null, ieIndicator: null, finalConsumer: null }),
    "Perfil de destinatário criado com sucesso.",
  );
  const createGroup = useFiscalAction<Record<string, unknown>>(
    () => "/grupos",
    "POST",
    () => setG({ code: "", name: "" }),
    "Grupo tributário criado com sucesso.",
  );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card title="Perfis de destinatário">
        <p className="mb-2 text-xs text-muted-foreground">
          Usados na conferência de cobertura da matriz (prontidão) e como atalho nas condições das
          regras.
        </p>
        <Table head={["Nome", "PF/PJ", "IE", "Consumidor final"]}>
          {profiles.data?.map((x) => (
            <tr key={x.id}>
              <td className="px-3 py-2">{x.name}</td>
              <td className="px-3 py-2">{x.personKind ?? "qualquer"}</td>
              <td className="px-3 py-2">{x.ieIndicator ?? "qualquer"}</td>
              <td className="px-3 py-2">
                {x.finalConsumer === null ? "qualquer" : x.finalConsumer ? "sim" : "não"}
              </td>
            </tr>
          ))}
        </Table>
        {can("fiscal.configuracao.editar") && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <TextInput label="Nome" value={p.name} onChange={(v) => setP({ ...p, name: v })} />
            <Choice
              label="PF/PJ"
              value={p.personKind}
              empty="Qualquer"
              options={[
                ["pf", "PF"],
                ["pj", "PJ"],
              ]}
              onChange={(v) => setP({ ...p, personKind: v })}
            />
            <Choice
              label="Indicador de IE"
              value={p.ieIndicator}
              empty="Qualquer"
              options={[
                ["1", "Contribuinte"],
                ["2", "Isento"],
                ["9", "Não contribuinte"],
              ]}
              onChange={(v) => setP({ ...p, ieIndicator: v })}
            />
            <Choice
              label="Consumidor final"
              value={p.finalConsumer}
              empty="Qualquer"
              options={[
                ["true", "Sim"],
                ["false", "Não"],
              ]}
              onChange={(v) => setP({ ...p, finalConsumer: v })}
            />
            <Button
              onClick={() =>
                createProfile.mutate({
                  name: p.name,
                  personKind: p.personKind,
                  ieIndicator: p.ieIndicator,
                  finalConsumer: p.finalConsumer === null ? null : p.finalConsumer === "true",
                })
              }
            >
              Adicionar perfil
            </Button>
          </div>
        )}
        <SaveError error={createProfile.error} />
      </Card>
      <Card title="Grupos tributários de produto">
        <p className="mb-2 text-xs text-muted-foreground">
          Ligue o produto ao grupo em {MENU_PATH.produtos}; as regras podem usar o grupo como
          condição.
        </p>
        <Table head={["Código", "Nome", "Produtos"]}>
          {groups.data?.map((x) => (
            <tr key={x.id}>
              <td className="px-3 py-2 font-mono">{x.code}</td>
              <td className="px-3 py-2">{x.name}</td>
              <td className="px-3 py-2">{x.productCount}</td>
            </tr>
          ))}
        </Table>
        {can("fiscal.matriz.preparar") && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <TextInput
              label="Código"
              value={g.code}
              onChange={(v) => setG({ ...g, code: v.toUpperCase() })}
            />
            <TextInput label="Nome" value={g.name} onChange={(v) => setG({ ...g, name: v })} />
            <Button onClick={() => createGroup.mutate({ ...g, description: null })}>
              Adicionar grupo
            </Button>
          </div>
        )}
        <SaveError error={createGroup.error} />
      </Card>
    </div>
  );
}
